/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import CallMadeIcon from '@material-ui/icons/CallMade';
import styled from 'styled-components';
import { JobContext } from 'context/jobContextContainer';
import { CallContext } from 'context/callContextContainer';
import { routePlanService } from 'services/routePlanService';
import { CircularProgressContext } from 'components/CircularProgressOverlay';

const moment = require('moment-timezone');

const tableCellPadding = '4px 6px';

const StyledTableContainer = styled(TableContainer)`
  max-height: 400px;
  overflow: auto;
  margin-bottom: 20px;
  & .MuiTableCell-root {
    padding: ${tableCellPadding};
  }
`;

const StyledTableHeadCell = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  background: #f5f5f5;
  padding: ${tableCellPadding} !important;
`;

const StyledTableHeadCellWrap = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: normal;
  background: #f5f5f5;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCell = styled(TableCell)`
  font-size: 12px;
  white-space: nowrap;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCellWrap = styled(TableCell)`
  font-size: 12px;
  white-space: normal;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCellLocation = styled(TableCell)`
  font-size: 12px;
  white-space: normal;
  min-width: 180px;
  padding: ${tableCellPadding} !important;
`;

const StyledTableHeadCellLocation = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: normal;
  min-width: 180px;
  background: #f5f5f5;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCellTime = styled(TableCell)`
  font-size: 12px;
  white-space: normal;
  max-width: 80px;
  padding: ${tableCellPadding} !important;
`;

const StyledTableHeadCellTime = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: normal;
  max-width: 80px;
  background: #f5f5f5;
  padding: ${tableCellPadding} !important;
`;

const VehicleSection = styled(Box)`
  margin-bottom: 30px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  background: #fafafa;
`;

const StatsBox = styled(Box)`
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  padding: 10px;
  background: white;
  border-radius: 4px;
`;

const StatItem = styled.div`
  font-size: 13px;
  & strong {
    margin-right: 5px;
  }
`;

const UnassignedBox = styled(Box)`
  margin-top: 20px;
  padding: 15px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
`;

const CalculationStepContainer = styled.div`
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 10px;
`;

const CalculationStep = forwardRef(({ isActive }, ref) => {
  const { routePlanData, driverList } = useContext(JobContext);
  const { siteSetting } = useContext(CallContext);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculationError, setCalculationError] = useState(null);
  const [jobMoves, setJobMoves] = useState({}); // { [jobId]: targetVehicleId }
  const [unassignedByUser, setUnassignedByUser] = useState({}); // { [jobId]: { fromVehicleId, fromVehicleName } }
  const [selectedJobsByVehicle, setSelectedJobsByVehicle] = useState({}); // { [vehicleKey]: jobId[] }
  const [driverAssignments, setDriverAssignments] = useState({}); // { [vehicleKey]: driverId }
  const [moveDialog, setMoveDialog] = useState({
    open: false,
    sourceVehicleId: null,
    jobIds: [],
  });
  const [moveTargetVehicleId, setMoveTargetVehicleId] = useState(null);
  const previousRoutePlanDataRef = useRef(null);

  const calculation = async () => {
    const jobs = routePlanData?.jobs || [];
    const vehicles = routePlanData?.vehicles || [];

    if (jobs.length === 0 || vehicles.length === 0) {
      console.log('No jobs or vehicles to calculate');
      return;
    }

    try {
      showCircularProgress('Calculating route...');
      setCalculationError(null);
      const response = await routePlanService.route_plan_calculation(jobs, vehicles);
      if (response?.data?.status === 'SUCCESS') {
        setCalculationResult(response.data.data);
        setJobMoves({});
        setUnassignedByUser({});
        setSelectedJobsByVehicle({});
        setDriverAssignments({});
      } else {
        setCalculationError(response?.data?.message || 'Calculation failed');
      }
    } catch (error) {
      console.error('Error calculating route:', error);
      setCalculationError(error?.response?.data?.message || error?.message || 'An error occurred during calculation');
    } finally {
      hideCircularProgress();
    }
  };

  useEffect(() => {
    if (isActive) {
      // Check if routePlanData has changed by comparing jobs and vehicles
      const currentJobs = routePlanData?.jobs || [];
      const currentVehicles = routePlanData?.vehicles || [];
      const previousJobs = previousRoutePlanDataRef.current?.jobs || [];
      const previousVehicles = previousRoutePlanDataRef.current?.vehicles || [];

      // Compare job IDs to detect changes (jobs are just selected/deselected)
      const jobsChanged = 
        currentJobs.length !== previousJobs.length ||
        currentJobs.some((job, index) => job.id !== previousJobs[index]?.id) ||
        previousJobs.some((job, index) => job.id !== currentJobs[index]?.id);

      // Compare full vehicle objects to detect schedule changes (vehicles can have schedule info updated)
      const vehiclesChanged = 
        currentVehicles.length !== previousVehicles.length ||
        currentVehicles.some((vehicle) => {
          const previousVehicle = previousVehicles.find(v => v.id === vehicle.id);
          if (!previousVehicle) return true; // New vehicle
          // Compare full vehicle object including schedule
          return JSON.stringify(vehicle) !== JSON.stringify(previousVehicle);
        }) ||
        previousVehicles.some((vehicle) => {
          const currentVehicle = currentVehicles.find(v => v.id === vehicle.id);
          return !currentVehicle; // Vehicle removed
        });

      const dataChanged = !previousRoutePlanDataRef.current || jobsChanged || vehiclesChanged;

      // Calculate if hasn't been calculated yet OR if data has changed
      if (!hasCalculated || dataChanged) {
        calculation();
        setHasCalculated(true);
        // Deep copy to store previous state
        previousRoutePlanDataRef.current = {
          jobs: currentJobs.map(job => ({ ...job })),
          vehicles: currentVehicles.map(vehicle => ({ ...vehicle })),
        };
      }
    }
  }, [isActive, routePlanData, hasCalculated]);

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === '-') return '-';
    const m = moment.utc(dateString);
    if (!m.isValid()) return '-';
    const setting = siteSetting();
    if (!setting || !setting.timezone) {
      return m.format('MM/DD/YYYY hh:mm A');
    }
    return m.tz(setting.timezone).format('MM/DD/YYYY hh:mm A');
  };

  const formatDateTimeTwoLines = (dateString) => {
    if (!dateString || dateString === '-') return { date: '-', time: '' };
    const m = moment.utc(dateString);
    if (!m.isValid()) return { date: '-', time: '' };
    const setting = siteSetting();
    const fmt = setting?.timezone ? m.tz(setting.timezone) : m;
    return {
      date: fmt.format('MM/DD/YYYY'),
      time: fmt.format('hh:mm A'),
    };
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDistance = (meters) => {
    const miles = (meters / 1609.34).toFixed(2);
    return `${miles} mi`;
  };

  const getActivityTypeLabel = (type) => {
    const labels = {
      departure: 'Departure',
      pickup: 'Pickup',
      delivery: 'Dropoff',
      arrival: 'Arrival',
    };
    return labels[type] || type;
  };

  const formatLocationAddress = (location) => {
    if (!location) return '-';
    const parts = [
      location.place_name || location.location_name,
      location.address || location.addr1,
      location.city,
      location.state,
      location.zip,
    ].filter(str => str?.length > 0);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const getVehicleDisplayName = (tourOrVehicle) => {
    const vehicle = tourOrVehicle.vehicle || tourOrVehicle;
    if (vehicle) {
      const vehicleNo = vehicle.vehicle_no || tourOrVehicle.vehicleId || tourOrVehicle.typeId || '-';
      const dmvLicense = vehicle.dmv_license;
      return dmvLicense ? `${vehicleNo} (${dmvLicense})` : vehicleNo;
    }
    return tourOrVehicle.vehicleId || tourOrVehicle.typeId || '-';
  };

  const formatJobId = (jobId) => {
    if (!jobId) return '-';
    return String(jobId).replace(/^job_/, '');
  };

  const getPassengerNameForJob = (jobId) => {
    const jobIdNum = formatJobId(jobId);
    if (!jobIdNum || jobIdNum === 'departure' || jobIdNum === 'arrival') return '-';
    const job = (routePlanData?.jobs || []).find(j => String(j.id) === jobIdNum);
    return job?.passenger_name || '-';
  };

  const getVehicleKey = (section) =>
    String(section.vehicle?.id || section.vehicleId || section.typeId || '');

  const getJobIdsFromSection = (section) => {
    const jobIds = new Set();
    (section.stops || []).forEach(stop => {
      (stop.activities || []).forEach(act => {
        const jid = act.jobId ? formatJobId(act.jobId) : null;
        if (jid && jid !== 'departure' && jid !== 'arrival') jobIds.add(jid);
      });
    });
    return Array.from(jobIds);
  };

  const applyJobMoves = (sections) => {
    const hasMoves = Object.keys(jobMoves).length > 0;
    const hasUnassigned = Object.keys(unassignedByUser).length > 0;
    if (!sections || (!hasMoves && !hasUnassigned)) return sections;
    const jobsMap = {};
    (routePlanData?.jobs || []).forEach(j => {
      jobsMap[String(j.id)] = j;
    });
    return sections.map(section => {
      const vkey = getVehicleKey(section);
      const newStops = [];
      (section.stops || []).forEach(stop => {
        const newActivities = (stop.activities || []).filter(act => {
          const jid = act.jobId ? formatJobId(act.jobId) : null;
          if (!jid || jid === 'departure' || jid === 'arrival') return true;
          if (unassignedByUser[jid]) return false; // User unassigned - remove from vehicle
          const target = jobMoves[jid];
          return !target || target === vkey;
        });
        if (newActivities.length > 0) {
          newStops.push({ ...stop, activities: newActivities });
        }
      });
      const movedToThisVehicle = Object.entries(jobMoves)
        .filter(([jid, targetId]) => String(targetId) === vkey && !unassignedByUser[jid])
        .map(([jid]) => jid);
      const toTimeStr = (raw) => {
        if (!raw || raw === '0000-00-00 00:00:00') return null;
        return raw.replace(' ', 'T') + 'Z';
      };
      const movedStops = [];
      movedToThisVehicle.forEach(jid => {
        const job = jobsMap[jid];
        const pickupTime = toTimeStr(job?.reservation_time);
        const dropoffTime = toTimeStr(job?.reservation_end_time);
        if (job?.pickup) {
          movedStops.push({
            activities: [{ type: 'pickup', jobId: `job_${jid}`, location: job.pickup }],
            time: {
              arrival: pickupTime || '-',
              departure: pickupTime || '-',
            },
            load: [1],
            distance: 0,
          });
        }
        if (job?.dropoff) {
          movedStops.push({
            activities: [{ type: 'delivery', jobId: `job_${jid}`, location: job.dropoff }],
            time: {
              arrival: dropoffTime || '-',
              departure: dropoffTime || '-',
            },
            load: [0],
            distance: 0,
          });
        }
      });
      const allStops = [...newStops, ...movedStops];
      const getStopSortTime = (stop) => {
        const t = stop.time?.arrival || stop.time?.departure;
        if (!t || t === '-') return Infinity;
        const m = moment.utc(t);
        return m.isValid() ? m.valueOf() : Infinity;
      };
      allStops.sort((a, b) => getStopSortTime(a) - getStopSortTime(b));
      return { ...section, stops: allStops };
    });
  };

  const handleSelectJob = (vehicleKey, jobId) => {
    setSelectedJobsByVehicle(prev => {
      const arr = prev[vehicleKey] || [];
      const idx = arr.indexOf(jobId);
      const next = idx >= 0 ? arr.filter((_, i) => i !== idx) : [...arr, jobId];
      return { ...prev, [vehicleKey]: next };
    });
  };

  const handleSelectAllJobs = (vehicleKey, checked, sections) => {
    const section = (sections || []).find(s => getVehicleKey(s) === vehicleKey);
    if (!section) return;
    const jobIds = getJobIdsFromSection(section);
    setSelectedJobsByVehicle(prev => ({
      ...prev,
      [vehicleKey]: checked ? jobIds : [],
    }));
  };

  const handleMoveClick = (vehicleKey, jobIds) => {
    if (!jobIds || jobIds.length === 0) return;
    setMoveDialog({ open: true, sourceVehicleId: vehicleKey, jobIds });
    setMoveTargetVehicleId(null);
  };

  const handleMoveConfirm = (sourceVehicleSection, originalJobVehicleMap) => {
    if (!moveTargetVehicleId || !moveDialog.jobIds?.length) return;
    if (moveTargetVehicleId === '__unassign__') {
      handleUnassignJobs(moveDialog.jobIds, moveDialog.sourceVehicleId, originalJobVehicleMap);
      setMoveDialog({ open: false, sourceVehicleId: null, jobIds: [] });
      setMoveTargetVehicleId(null);
      return;
    }
    setJobMoves(prev => {
      const next = { ...prev };
      moveDialog.jobIds.forEach(jid => {
        next[jid] = moveTargetVehicleId;
      });
      return next;
    });
    setUnassignedByUser(prev => {
      const next = { ...prev };
      moveDialog.jobIds.forEach(jid => {
        delete next[jid];
      });
      return next;
    });
    if (moveDialog.sourceVehicleId) {
      setSelectedJobsByVehicle(prev => ({
        ...prev,
        [moveDialog.sourceVehicleId]: [],
      }));
    }
    setMoveDialog({ open: false, sourceVehicleId: null, jobIds: [] });
    setMoveTargetVehicleId(null);
  };

  const handleAssignUnassignedJob = (jobId) => {
    const jid = formatJobId(jobId);
    if (!jid) return;
    setMoveDialog({ open: true, sourceVehicleId: null, jobIds: [jid] });
    setMoveTargetVehicleId(null);
  };

  const handleUnassignJobs = (jobIds, currentVehicleKey, originalJobVehicleMap) => {
    if (!jobIds || jobIds.length === 0) return;
    setUnassignedByUser(prev => {
      const next = { ...prev };
      jobIds.forEach(jid => {
        const orig = originalJobVehicleMap?.[jid];
        const fromVehicleName = orig ? getVehicleDisplayName(orig.section) : null;
        next[jid] = { fromVehicleId: currentVehicleKey, fromVehicleName };
      });
      return next;
    });
    setJobMoves(prev => {
      const next = { ...prev };
      jobIds.forEach(jid => delete next[jid]);
      return next;
    });
    setSelectedJobsByVehicle(prev => ({
      ...prev,
      [currentVehicleKey]: [],
    }));
  };

  const handleMoveDialogClose = () => {
    setMoveDialog({ open: false, sourceVehicleId: null, jobIds: [] });
    setMoveTargetVehicleId(null);
  };

  const handleDriverChange = (vehicleKey, driverId) => {
    setDriverAssignments(prev => {
      const next = { ...prev };
      if (driverId && driverId !== '') {
        next[vehicleKey] = driverId;
      } else {
        delete next[vehicleKey];
      }
      return next;
    });
  };

  const getAvailableDrivers = (currentVehicleKey, allAssignments) => {
    const currentVehicleDriverId = allAssignments[currentVehicleKey];
    
    return (driverList || []).filter(driver => {
      // Include driver if:
      // 1. It's assigned to the current vehicle (so user can change/unassign), OR
      // 2. It's not assigned to any other vehicle
      if (driver.id === currentVehicleDriverId) {
        return true; // Current vehicle's driver is always available
      }
      // Check if driver is assigned to any other vehicle
      const isAssignedToOtherVehicle = Object.entries(allAssignments).some(
        ([vKey, driverId]) => vKey !== currentVehicleKey && driverId === driver.id
      );
      return !isAssignedToOtherVehicle;
    });
  };

  const renderVehicleTable = (tourOrVehicle, index, displaySections, originalJobVehicleMap) => {
    const stats = tourOrVehicle.statistic || {};
    const times = stats.times || {};
    const isEmptyVehicle = !tourOrVehicle.stops || tourOrVehicle.stops.length === 0;
    const vehicleKey = getVehicleKey(tourOrVehicle);
    const selectedJobIds = selectedJobsByVehicle[vehicleKey] || [];
    const jobIdsInSection = getJobIdsFromSection(tourOrVehicle);
    const numSelectable = jobIdsInSection.length;
    const numSelected = selectedJobIds.length;
    const isAllSelected = numSelectable > 0 && numSelected === numSelectable;
    const isIndeterminate = numSelected > 0 && numSelected < numSelectable;

    const isJobRow = (act) => {
      const jid = act.jobId ? formatJobId(act.jobId) : null;
      return jid && jid !== 'departure' && jid !== 'arrival';
    };

    const isJobSelected = (act) => {
      const jid = act.jobId ? formatJobId(act.jobId) : null;
      return jid && selectedJobIds.includes(jid);
    };

    const availableDrivers = getAvailableDrivers(vehicleKey, driverAssignments);
    const assignedDriverId = driverAssignments[vehicleKey] || '';
    const hasJobs = jobIdsInSection.length > 0;

    return (
      <VehicleSection key={tourOrVehicle.vehicleId || tourOrVehicle.vehicle?.id || index}>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <Typography variant="h6">
            Vehicle: {getVehicleDisplayName(tourOrVehicle)}
            {isEmptyVehicle && (
              <Typography component="span" style={{ fontSize: '14px', color: '#666', fontWeight: 400, marginLeft: '8px' }}>
                (Not needed - no jobs assigned)
              </Typography>
            )}
          </Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }} disabled={!hasJobs}>
            <InputLabel>Driver</InputLabel>
            <Select
              value={assignedDriverId}
              onChange={(e) => handleDriverChange(vehicleKey, e.target.value)}
              label="Driver"
              disabled={!hasJobs}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableDrivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <StatsBox>
          <StatItem>
            <strong>Distance:</strong> {formatDistance(stats.distance || 0)}
          </StatItem>
          <StatItem>
            <strong>Duration:</strong> {formatDuration(stats.duration || 0)}
          </StatItem>
          <StatItem>
            <strong>Driving:</strong> {formatDuration(times.driving || 0)}
          </StatItem>
          <StatItem>
            <strong>Serving:</strong> {formatDuration(times.serving || 0)}
          </StatItem>
          <StatItem>
            <strong>Waiting:</strong> {formatDuration(times.waiting || 0)}
          </StatItem>
        </StatsBox>

        <StyledTableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableHeadCell padding="checkbox" align="center">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAllJobs(vehicleKey, e.target.checked, displaySections)}
                    disabled={numSelectable === 0}
                    inputProps={{ 'aria-label': 'select all jobs' }}
                  />
                </StyledTableHeadCell>
                <StyledTableHeadCell align="center">#</StyledTableHeadCell>
                <StyledTableHeadCellTime align="center">Arrival Time</StyledTableHeadCellTime>
                <StyledTableHeadCellTime align="center">Departure Time</StyledTableHeadCellTime>
                <StyledTableHeadCell align="center">Activity</StyledTableHeadCell>
                <StyledTableHeadCell align="center">Job ID</StyledTableHeadCell>
                <StyledTableHeadCell align="center">Passenger</StyledTableHeadCell>
                <StyledTableHeadCellLocation align="center">Location</StyledTableHeadCellLocation>
                <StyledTableHeadCell align="center">Load</StyledTableHeadCell>
                <StyledTableHeadCell align="center">Distance</StyledTableHeadCell>
                <StyledTableHeadCell align="center">
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<CallMadeIcon fontSize="small" />}
                    onClick={() => handleMoveClick(vehicleKey, selectedJobIds)}
                    disabled={numSelected === 0}
                    style={{ minWidth: 'auto', padding: '4px 8px' }}
                  >
                    Move
                  </Button>
                </StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const allStops = tourOrVehicle.stops || [];
                const hasAnyJobs = allStops.some((stop) =>
                  (stop.activities || []).some((act) => act.type !== 'departure' && act.type !== 'arrival')
                );
                if (!hasAnyJobs || allStops.length === 0) {
                  return (
                    <TableRow>
                      <StyledTableCell colSpan={11} align="center">
                        No jobs
                      </StyledTableCell>
                    </TableRow>
                  );
                }
                return allStops.map((stop, stopIndex) => {
                  const activities = stop.activities || [];
                  return activities.map((activity, activityIndex) => {
                    const jid = activity.jobId ? formatJobId(activity.jobId) : null;
                    const hasJob = isJobRow(activity);
                    const checked = hasJob && isJobSelected(activity);

                    return (
                      <TableRow
                        key={`${stopIndex}-${activityIndex}`}
                        onClick={hasJob ? () => handleSelectJob(vehicleKey, jid) : undefined}
                        style={hasJob ? { cursor: 'pointer' } : undefined}
                      >
                        <StyledTableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                          {hasJob ? (
                            <Checkbox
                              checked={checked}
                              onChange={() => handleSelectJob(vehicleKey, jid)}
                              inputProps={{ 'aria-label': `select job ${jid}` }}
                            />
                          ) : (
                            <Checkbox disabled checked={false} />
                          )}
                        </StyledTableCell>
                        {activityIndex === 0 && (
                          <>
                            <StyledTableCell align="center" rowSpan={activities.length}>
                              {stopIndex + 1}
                            </StyledTableCell>
                            <StyledTableCellTime align="center" rowSpan={activities.length}>
                              {(() => {
                                const { date, time } = formatDateTimeTwoLines(stop.time?.arrival);
                                return (
                                  <>
                                    <div>{date}</div>
                                    {time && <div>{time}</div>}
                                  </>
                                );
                              })()}
                            </StyledTableCellTime>
                            <StyledTableCellTime align="center" rowSpan={activities.length}>
                              {(() => {
                                const { date, time } = formatDateTimeTwoLines(stop.time?.departure);
                                return (
                                  <>
                                    <div>{date}</div>
                                    {time && <div>{time}</div>}
                                  </>
                                );
                              })()}
                            </StyledTableCellTime>
                          </>
                        )}
                        <StyledTableCell align="center">
                          {getActivityTypeLabel(activity.type)}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {formatJobId(activity.jobId)}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {getPassengerNameForJob(activity.jobId)}
                        </StyledTableCell>
                        <StyledTableCellLocation align="center">
                          {formatLocationAddress(activity.location)}
                        </StyledTableCellLocation>
                        {activityIndex === 0 && (
                          <>
                            <StyledTableCell align="center" rowSpan={activities.length}>
                              {stop.load?.[0] || 0}
                            </StyledTableCell>
                            <StyledTableCell align="center" rowSpan={activities.length}>
                              {formatDistance(stop.distance || 0)}
                            </StyledTableCell>
                          </>
                        )}
                        <StyledTableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {hasJob ? (
                            <Box>
                              {jobMoves[jid] === vehicleKey &&
                                (originalJobVehicleMap?.[jid]?.key !== vehicleKey) && (
                                <Typography variant="caption" display="block" style={{ color: '#666', marginBottom: '4px' }}>
                                  From: {originalJobVehicleMap?.[jid]
                                    ? getVehicleDisplayName(originalJobVehicleMap[jid].section)
                                    : 'Unassigned'}
                                </Typography>
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CallMadeIcon fontSize="small" />}
                                onClick={(e) => { e.stopPropagation(); handleMoveClick(vehicleKey, [jid]); }}
                                style={{ minWidth: 'auto', padding: '2px 6px' }}
                              >
                                Move
                              </Button>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </StyledTableCell>
                      </TableRow>
                    );
                  });
                });
              })()}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </VehicleSection>
    );
  };

  const renderUnassignedJobs = (unassigned) => {
    const apiUnassigned = (unassigned || []).filter((item) => {
      const jid = formatJobId(item.jobId);
      return !jobMoves[jid] && !unassignedByUser[jid]; // Not assigned, and not manually unassigned (avoid duplicate)
    });
    const manuallyUnassigned = Object.entries(unassignedByUser).map(([jid, info]) => {
      const apiItem = (unassigned || []).find((item) => formatJobId(item.jobId) === jid);
      return {
        jobId: `job_${jid}`,
        job: (routePlanData?.jobs || []).find(j => String(j.id) === jid),
        manuallyUnassigned: true,
        fromVehicleName: info.fromVehicleName,
        reasons: apiItem?.reasons,
      };
    });
    const allUnassigned = [...apiUnassigned, ...manuallyUnassigned];
    if (allUnassigned.length === 0) return null;

    return (
      <UnassignedBox>
        <Typography variant="h6" style={{ marginBottom: '10px', color: '#856404' }}>
          Unassigned Jobs
        </Typography>
        {allUnassigned.map((item, index) => {
          const job = item.job;
          const jid = formatJobId(item.jobId);
          const pickupAddr = job?.pickup ? formatLocationAddress(job.pickup) : '-';
          const dropoffAddr = job?.dropoff ? formatLocationAddress(job.dropoff) : '-';
          const reservationTime = job?.reservation_time && job.reservation_time !== '0000-00-00 00:00:00'
            ? formatDateTime(job.reservation_time.replace(' ', 'T') + 'Z')
            : '-';

          return (
            <Box key={`${jid}-${index}`} style={{ marginBottom: '15px', padding: '12px', background: 'white', borderRadius: '4px', border: '1px solid #ffc107', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <Box style={{ flex: 1 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Job #{jid}
                </Typography>
                {(item.reasons && item.reasons.length > 0) || item.manuallyUnassigned ? (
                  <Box style={{ marginBottom: '8px', fontSize: '12px' }}>
                    {item.reasons && item.reasons.length > 0 && (
                      <Box style={{ color: '#d32f2f', marginBottom: item.manuallyUnassigned ? '4px' : 0 }}>
                        {item.reasons.map((reason, reasonIndex) => (
                          <div key={reasonIndex}>
                            <strong>{reason.code}:</strong> {reason.description}
                          </div>
                        ))}
                      </Box>
                    )}
                    {item.manuallyUnassigned && item.fromVehicleName && (
                      <Box style={{ color: '#1976d2', fontStyle: 'italic' }}>
                        User manually unassigned it. Originally from: {item.fromVehicleName}
                      </Box>
                    )}
                  </Box>
                ) : null}
                <Box style={{ fontSize: '13px', lineHeight: 1.6 }}>
                  {job?.passenger_name && (
                    <div><strong>Passenger:</strong> {job.passenger_name}</div>
                  )}
                  <div><strong>Pickup:</strong> {pickupAddr}</div>
                  <div><strong>Dropoff:</strong> {dropoffAddr}</div>
                  {reservationTime !== '-' && (
                    <div><strong>Reservation Time:</strong> {reservationTime}</div>
                  )}
                </Box>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CallMadeIcon fontSize="small" />}
                onClick={() => handleAssignUnassignedJob(item.jobId)}
                style={{ flexShrink: 0 }}
              >
                Assign
              </Button>
            </Box>
          );
        })}
      </UnassignedBox>
    );
  };

  const hereResponse = calculationResult?.here_response;
  const tours = hereResponse?.tours || [];
  const unassigned = hereResponse?.unassigned || [];
  const overallStats = hereResponse?.statistic;

  // Build full list: tours first, then vehicles not in tours (empty vehicles)
  const assignedVehicleIds = new Set(
    tours.map(t => String(t.vehicle?.id || t.typeId))
  );
  const selectedVehicles = routePlanData?.vehicles || [];
  const unassignedVehicles = selectedVehicles.filter(
    v => !assignedVehicleIds.has(String(v.id))
  );
  const allVehicleSections = [
    ...tours,
    ...unassignedVehicles.map(v => ({
      vehicle: v,
      vehicleId: `${v.id}_0`,
      typeId: String(v.id),
      stops: [],
      statistic: {},
    })),
  ];
  const displaySections = applyJobMoves(allVehicleSections);

  // Map jobId -> original vehicle (from HERE API, before any moves)
  const originalJobVehicle = {};
  allVehicleSections.forEach((section) => {
    const vkey = getVehicleKey(section);
    getJobIdsFromSection(section).forEach((jid) => {
      originalJobVehicle[jid] = { key: vkey, section };
    });
  });

  const targetVehiclesForMove = moveDialog.sourceVehicleId == null
    ? displaySections
    : displaySections.filter(s => getVehicleKey(s) !== moveDialog.sourceVehicleId);

  const getFinishData = () => {
    // Recompute displaySections to ensure we have the latest state
    if (!calculationResult || !hereResponse) {
      return [];
    }

    const tours = hereResponse.tours || [];
    const assignedVehicleIds = new Set(
      tours.map(t => String(t.vehicle?.id || t.typeId))
    );
    const selectedVehicles = routePlanData?.vehicles || [];
    const unassignedVehicles = selectedVehicles.filter(
      v => !assignedVehicleIds.has(String(v.id))
    );
    const allVehicleSections = [
      ...tours,
      ...unassignedVehicles.map(v => ({
        vehicle: v,
        vehicleId: `${v.id}_0`,
        typeId: String(v.id),
        stops: [],
        statistic: {},
      })),
    ];
    const currentDisplaySections = applyJobMoves(allVehicleSections);

    if (!currentDisplaySections || currentDisplaySections.length === 0) {
      return [];
    }

    return currentDisplaySections
      .map((section) => {
        const vehicleKey = getVehicleKey(section);
        const vehicleId = section.vehicle?.id || section.vehicleId || section.typeId;
        const driverId = driverAssignments[vehicleKey] || null;
        const jobIds = getJobIdsFromSection(section);

        // Only include vehicles that have at least one job assigned
        if (!jobIds || jobIds.length === 0) {
          return null;
        }

        return {
          vehicle_id: vehicleId ? parseInt(vehicleId) : null,
          driver_id: driverId ? parseInt(driverId) : null,
          job_ids: jobIds.map(jid => parseInt(jid)),
        };
      })
      .filter(item => item !== null); // Remove vehicles with no jobs
  };

  const validate = () => {
    if (!calculationResult || !hereResponse) {
      return { isValid: false, message: 'No calculation results available' };
    }

    const tours = hereResponse.tours || [];
    const assignedVehicleIds = new Set(
      tours.map(t => String(t.vehicle?.id || t.typeId))
    );
    const selectedVehicles = routePlanData?.vehicles || [];
    const unassignedVehicles = selectedVehicles.filter(
      v => !assignedVehicleIds.has(String(v.id))
    );
    const allVehicleSections = [
      ...tours,
      ...unassignedVehicles.map(v => ({
        vehicle: v,
        vehicleId: `${v.id}_0`,
        typeId: String(v.id),
        stops: [],
        statistic: {},
      })),
    ];
    const currentDisplaySections = applyJobMoves(allVehicleSections);

    if (!currentDisplaySections || currentDisplaySections.length === 0) {
      return { isValid: false, message: 'No vehicles available' };
    }

    // Check each vehicle that has jobs assigned
    const vehiclesWithoutDrivers = [];
    currentDisplaySections.forEach((section) => {
      const vehicleKey = getVehicleKey(section);
      const jobIds = getJobIdsFromSection(section);
      
      // Only check vehicles that have jobs assigned
      if (jobIds && jobIds.length > 0) {
        const driverId = driverAssignments[vehicleKey];
        if (!driverId) {
          const vehicleName = getVehicleDisplayName(section);
          vehiclesWithoutDrivers.push(vehicleName);
        }
      }
    });

    if (vehiclesWithoutDrivers.length > 0) {
      return {
        isValid: false,
        message: `Please assign drivers to all vehicles with jobs. Missing drivers for: ${vehiclesWithoutDrivers.join(', ')}`,
      };
    }

    return { isValid: true };
  };

  useImperativeHandle(ref, () => ({
    getFinishData,
    validate,
  }));

  return (
    <CalculationStepContainer>
      <Dialog
        open={moveDialog.open}
        onClose={handleMoveDialogClose}
        aria-labelledby="move-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="move-dialog-title">
          {moveDialog.sourceVehicleId == null
            ? `Assign ${moveDialog.jobIds?.length || 0} job(s) to vehicle`
            : `Move ${moveDialog.jobIds?.length || 0} job(s) to vehicle`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
            Select target vehicle:
          </Typography>
          <List dense>
            {targetVehiclesForMove.map((section) => {
              const vkey = getVehicleKey(section);
              const label = getVehicleDisplayName(section);
              const isSelected = moveTargetVehicleId === vkey;
              return (
                <ListItem
                  key={vkey}
                  button
                  selected={isSelected}
                  onClick={() => setMoveTargetVehicleId(vkey)}
                >
                  <ListItemText primary={label} />
                </ListItem>
              );
            })}
            {moveDialog.sourceVehicleId != null && (
              <ListItem
                key="__unassign__"
                button
                selected={moveTargetVehicleId === '__unassign__'}
                onClick={() => setMoveTargetVehicleId('__unassign__')}
                style={{ borderTop: '1px solid #eee', marginTop: '8px', paddingTop: '12px' }}
              >
                <ListItemText
                  primary="Unassign"
                  primaryTypographyProps={{ style: { color: '#d32f2f', fontWeight: 500 } }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMoveDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => handleMoveConfirm(
              moveDialog.sourceVehicleId
                ? displaySections.find(s => getVehicleKey(s) === moveDialog.sourceVehicleId)
                : null,
              originalJobVehicle
            )}
            color="primary"
            variant="contained"
            disabled={!moveTargetVehicleId}
          >
            {moveTargetVehicleId === '__unassign__'
              ? 'Unassign'
              : moveDialog.sourceVehicleId == null
                ? 'Assign'
                : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h6" style={{ marginBottom: '15px' }}>
        Route Planning Results
      </Typography>
      
      {calculationError && (
        <Box style={{ color: 'red', marginBottom: '15px', padding: '10px', background: '#ffebee', borderRadius: '4px' }}>
          <strong>Error:</strong> {calculationError}
        </Box>
      )}

      {calculationResult && hereResponse && (
        <>
          {overallStats && (
            <StatsBox style={{ marginBottom: '20px' }}>
              <StatItem>
                <strong>Total Distance:</strong> {formatDistance(overallStats.distance || 0)}
              </StatItem>
              <StatItem>
                <strong>Total Duration:</strong> {formatDuration(overallStats.duration || 0)}
              </StatItem>
            </StatsBox>
          )}

          {displaySections.length > 0 ? (
            displaySections.map((section, index) => renderVehicleTable(section, index, displaySections, originalJobVehicle))
          ) : (
            <Box style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              No routes calculated
            </Box>
          )}

          {renderUnassignedJobs(unassigned)}
        </>
      )}

      {!calculationResult && !calculationError && (
        <Box style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Waiting for calculation...
        </Box>
      )}
    </CalculationStepContainer>
  );
});

CalculationStep.displayName = 'CalculationStep';

export default CalculationStep;
